import { useMutation, useQuery } from '@apollo/client/react'

import { useStableCallback } from '@syconium/little-miss-figgy'

import { gql } from '../../__generated__/graphql/catalog'
import { useAuthentication } from '../../app/_providers/AuthenticationProvider.client'
import { useGraphqlClients } from '../../app/_providers/GraphqlClientsProvider.client'
import { useEmbroideryV2Flag } from '../../containers/EmbroideryContainer/useEmbroideryV2Flag'

// If the Operation name of this query changes, the refetch clauses in this file
// must also be updated to match that change.
export const EMBROIDERY_PREFERENCES_QUERY = gql(`
  query FetchEmbroideryPreferences {
    customerProfile {
      id
      embroideryPreferences {
        text {
          key
          firstLine
          secondLine
          fontKey
          placement {
            position
            location
          }
        }
        logo {
          key
          defaultLogoId
          placement {
            position
            location
          }
        }
        icon {
          key
          initials
        }
      }
    }
  }
`)

const UPDATE_TEXT_EMBROIDERY_PREFERENCES = gql(`
  mutation UpdateEmbroideryTextPreferences(
    $preferences: CustomerProfileTextEmbroideryPreferencesInput!
  ) {
    updateCustomerProfileTextEmbroideryPreferences(preferences: $preferences) {
      id
    }
  }
`)

const UPDATE_LOGO_EMBROIDERY_PREFERENCES = gql(`
  mutation UpdateEmbroideryLogoPreferences(
    $preferences: CustomerProfileLogoEmbroideryPreferencesInput!
  ) {
    updateCustomerProfileLogoEmbroideryPreferences(preferences: $preferences) {
      id
    }
  }
`)

type UseEmbroideryPreferencesProps = {
  skip: boolean
}

export function useEmbroideryPreferences(props?: UseEmbroideryPreferencesProps) {
  const { isLoggedIn } = useAuthentication()
  const { personalizedClient } = useGraphqlClients()
  const isMultiPlacementEnabled = useEmbroideryV2Flag({ skip: false })

  const result = useQuery(EMBROIDERY_PREFERENCES_QUERY, {
    client: personalizedClient,
    skip: props?.skip || !isLoggedIn || isMultiPlacementEnabled,
  })

  return {
    ...result,
    data: result.data?.customerProfile?.embroideryPreferences,
    previousData: result.previousData?.customerProfile?.embroideryPreferences,
    customerProfileId: result.data?.customerProfile?.id ?? result.previousData?.customerProfile?.id,
  }
}

export function useUpdateEmbroideryPreferences() {
  const { isLoggedIn } = useAuthentication()
  const { personalizedClient } = useGraphqlClients()
  const isMultiPlacementEnabled = useEmbroideryV2Flag({ skip: false })

  const [updateTextEmbroideryPreferencesImpl, updateTextEmbroideryPreferencesResult] = useMutation(
    UPDATE_TEXT_EMBROIDERY_PREFERENCES,
    {
      refetchQueries: ['FetchEmbroideryPreferences'],
      client: personalizedClient,
    }
  )
  const updateTextEmbroideryPreferences = useStableCallback(
    async (...params: Parameters<typeof updateTextEmbroideryPreferencesImpl>) => {
      if (isLoggedIn && !isMultiPlacementEnabled)
        return await updateTextEmbroideryPreferencesImpl(...params)
      return await Promise.resolve()
    }
  )

  const [updateLogoEmbroideryPreferencesImpl, updateLogoEmbroideryPreferencesResult] = useMutation(
    UPDATE_LOGO_EMBROIDERY_PREFERENCES,
    {
      refetchQueries: ['FetchEmbroideryPreferences'],
      client: personalizedClient,
    }
  )
  const updateLogoEmbroideryPreferences = useStableCallback(
    async (...params: Parameters<typeof updateLogoEmbroideryPreferencesImpl>) => {
      if (isLoggedIn && !isMultiPlacementEnabled)
        return await updateLogoEmbroideryPreferencesImpl(...params)
      return await Promise.resolve()
    }
  )

  return {
    updateTextEmbroideryPreferences,
    updateTextEmbroideryPreferencesResult,
    updateLogoEmbroideryPreferences,
    updateLogoEmbroideryPreferencesResult,
  }
}
